import {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteManyParams,
  DeleteManyResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyReferenceParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  RaRecord,
  UpdateManyParams,
  UpdateManyResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { axiosInstance } from "./axiosConfig";

const extractFiles = (object: Record<string, any>): File[] => {
  let files: File[] = [];
  for (let key of Object.keys(object)) {
    if (key === "rawFile" && object[key] instanceof File) {
      files.push(object[key]);
    } else if (object[key] instanceof Object) {
      files.push(...extractFiles(object[key]));
    }
  }

  return files;
};

const buildMultipart = (data: unknown) => {
  const files = extractFiles(data as Record<string, any>);

  const form = new FormData();
  const blob = new Blob([JSON.stringify(data)], {
    type: "application/json",
  });

  form.append("request", blob);
  files.forEach((file) => {
    form.append("files", file);
  });

  return form;
};

interface ConfigParams {
  meta?: { fileUpload?: boolean };
}

interface UpdateConfigParams<T = any> {
  data: Partial<T>;
  previousData?: T;
  meta?: { fileUpload?: boolean };
}

/**
 * Extends react-admin's standard DataProvider with the "config" resource
 * pattern used by the (currently unused, see src/common/editConfig)
 * singleton-resource Edit machinery: getConfig/updateConfig work like
 * getOne/update but without an id, since that resource only ever has one
 * record. See pl-back's controllers for the matching /api/v1/<resource>
 * endpoints.
 */
export interface AppDataProvider extends DataProvider {
  getConfig: (resource: string, params: ConfigParams) => Promise<{ data: any }>;
  updateConfig: (
    resource: string,
    params: UpdateConfigParams
  ) => Promise<{ data: any }>;
}

export const dataProvider: AppDataProvider = {
  getList: <RecordType extends RaRecord = any>(
    resource: string,
    params: GetListParams
  ): Promise<GetListResult<RecordType>> => {
    let requestParams = {
      sortBy: params.sort.field,
      sortDirection: params.sort.order,
      pageNumber: params.pagination.page,
      pageSize: params.pagination.perPage,
      ...params.filter,
    };
    return axiosInstance
      .get(`/api/v1/${resource}`, { params: requestParams })
      .then((response) => {
        const data = response.data.map((item: any) => ({
          id: item._id,
          ...item,
        }));

        return { data, total: data.length };
      });
  },

  getConfig: (resource, params) => {
    let url = `/api/v1/${resource}`;

    return axiosInstance.get(url).then((response) => {
      return { data: response.data };
    });
  },

  getOne: <RecordType extends RaRecord = any>(
    resource: string,
    params: GetOneParams
  ): Promise<GetOneResult<RecordType>> => {
    let url = `/api/v1/${resource}/${params.id}`;

    return axiosInstance.get(url).then((response) => {
      return { data: { ...response.data, id: response.data._id } };
    });
  },

  getMany: <RecordType extends RaRecord = any>(
    resource: string,
    params: GetManyParams
  ): Promise<GetManyResult<RecordType>> => {
    return axiosInstance
      .get(`/api/v1/${resource}?ids=${params.ids}`)
      .then((response) => {
        return { data: response.data };
      });
  },

  getManyReference: (resource: string, params: GetManyReferenceParams) => {
    let requestParams: Record<string, any> = {
      sortBy: params.sort.field,
      sortDirection: params.sort.order,
      pageNumber: params.pagination.page,
      pageSize: params.pagination.perPage,
      ...params.filter,
    };
    requestParams[params.target] = params.id;
    return axiosInstance
      .get(`/api/v1/${resource}`, { params: requestParams })
      .then((response) => {
        return response.data;
      });
  },

  create: <
    RecordType extends Omit<RaRecord, "id"> = any,
    ResultRecordType extends RaRecord = RecordType & { id: any }
  >(
    resource: string,
    params: CreateParams
  ): Promise<CreateResult<ResultRecordType>> => {
    if (params?.meta?.fileUpload) {
      return axiosInstance
        .post(`/api/v1/${resource}`, buildMultipart(params.data))
        .then((response) => {
          return { data: { ...response.data, id: response.data._id } };
        });
    }

    return axiosInstance
      .post(`/api/v1/${resource}`, params.data)
      .then((response) => {
        return { data: response.data };
      });
  },

  updateConfig: (resource, params) => {
    if (params?.meta?.fileUpload) {
      return axiosInstance
        .put(`/api/v1/${resource}`, buildMultipart(params.data), {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          return { data: response.data };
        });
    }

    return axiosInstance
      .put(`/api/v1/${resource}`, params.data)
      .then((response) => {
        return { data: response.data };
      });
  },

  update: <RecordType extends RaRecord = any>(
    resource: string,
    params: UpdateParams
  ): Promise<UpdateResult<RecordType>> => {
    if (params?.meta?.fileUpload) {
      return axiosInstance
        .put(`/api/v1/${resource}/${params.id}`, buildMultipart(params.data))
        .then((response) => {
          return { data: { ...response.data, id: response.data._id } };
        });
    }

    return axiosInstance
      .put(`/api/v1/${resource}/${params.id}`, params.data)
      .then((response) => {
        return { data: response.data };
      });
  },

  updateMany: <RecordType extends RaRecord = any>(
    _resource: string,
    _params: UpdateManyParams
  ): Promise<UpdateManyResult<RecordType>> => {
    return Promise.resolve({});
  },

  delete: <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteParams<RecordType>
  ): Promise<DeleteResult<RecordType>> => {
    return axiosInstance
      .delete(`/api/v1/${resource}/${params.id}`)
      .then(() => {
        return { data: params.previousData as RecordType };
      });
  },

  deleteMany: <RecordType extends RaRecord = any>(
    resource: string,
    params: DeleteManyParams<RecordType>
  ): Promise<DeleteManyResult<RecordType>> => {
    const ids = params.ids;

    return axiosInstance
      .delete(`/api/v1/${resource}`, { params: { ids } })
      .then(() => {
        return { data: params.ids };
      });
  },
};
